<template>
  <div v-if="isOpen" class="fixed inset-0 top-[0rem] bg-black bg-opacity-50 flex items-center justify-center z-50 w-full h-full p-8">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative dark:bg-dark-box-bg dark:border dark:border-dark-border dark:text-dark-text max-h-[90vh] overflow-y-auto">
      <h2 class="text-2xl font-bold mb-4 text-gray-800 dark:text-dark-text">Edytuj zamówienie</h2>
      <button @click="closeModal" class="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none dark:text-dark-text dark:hover:text-dark-border-hover">
        <span class="text-xl">&times;</span>
      </button>
      <form @submit.prevent="submitForm" class="space-y-4">

        <!-- MPK -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">ID Zlecenia:</label>
          <input
              v-model="localOrder.mpk"
              type="text"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.mpk }"
              @input="validateField('mpk')"
              disabled
          />
          <span v-if="localErrors.mpk" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.mpk }}</span>
        </div>

        <!-- Firma -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Firma:</label>
          <select
              v-model="localOrder.companyId"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.companyId }"
              @change="validateField('companyId')"
              disabled
          >
            <option value="">Wybierz firmę</option>
            <option v-for="company in companies" :key="company.id" :value="company.id">
              {{ company.companyName }}
            </option>
          </select>
          <span v-if="localErrors.companyId" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.companyId }}</span>
        </div>

        <h3 class="text-lg font-semibold text-gray-800 dark:text-dark-text">Lista pasażerów</h3>

        <!-- Iteracja przez istniejących pasażerów -->
        <div v-for="(passenger, index) in localOrder.passenger" :key="index" class="mt-4 space-y-4 border-b pb-4 mb-4 border-gray-200 dark:border-dark-border">
          <div class="flex justify-between items-center">
            <p class="font-semibold text-gray-700 dark:text-dark-text">{{ passenger.firstName }} {{ passenger.lastName }}</p>

            <!-- Przyciski edytuj i usuń -->
            <div>
              <button @click.prevent="toggleEditMode(index)" class="text-blue-500 hover:text-blue-700 dark:text-dark-status-finished dark:hover:text-dark-status-available">
                {{ passenger.isEditing ? 'Zapisz' : 'Edytuj' }}
              </button>
              <button @click.prevent="removePassenger(index)" class="text-red-500 hover:text-red-700 dark:text-dark-status-unavailable dark:hover:text-dark-status-pending ml-2">
                Usuń
              </button>
            </div>
          </div>

          <!-- Dane pasażera widoczne tylko w trybie edycji -->
          <div v-if="passenger.isEditing" class="space-y-4">
            <!-- Godzina odbioru -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Godzina odbioru:</label>
              <input
                  v-model="passenger.pickupTime"
                  @click="showTimePicker"
                  type="time"
                  class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              />
            </div>

<!-- Adres Odbioru Opcje -->
<div>
  <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">
    Adres Odbioru:
  </label>
  <div class="space-y-2">
    <!-- Opcja: Adres domowy -->
    <div class="flex items-center">
      <input
        type="radio"
        :id="'pickup-home-' + index"
        v-model="passenger.pickupOption"
        value="home"
        @change="setPickupAddress(passenger)"
        class="mr-2"
      />
      <label :for="'pickup-home-' + index" class="text-gray-700 dark:text-dark-text">
        Do pracy (Adres zamieszkania)
      </label>
    </div>

    <!-- Opcja: Adres pracy -->
    <div class="flex items-center">
      <input
        type="radio"
        :id="'pickup-work-' + index"
        v-model="passenger.pickupOption"
        value="work"
        @change="setPickupAddress(passenger)"
        class="mr-2"
      />
      <label :for="'pickup-work-' + index" class="text-gray-700 dark:text-dark-text">
        Z pracy (Adres pracy)
      </label>
    </div>
    <input
      v-if="passenger.pickupOption === 'work'"
      v-model="passenger.pickupAddress"
      type="text"
      placeholder="Wpisz adres pracy"
      @input="filterAddressSuggestions(passenger, 'pickup')"
      @focus="showPickupSuggestions = true"
      @blur="hidePickupSuggestions(passenger)"
      class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
    />

    <ul v-if="showPickupSuggestions && passenger.pickupOption === 'work'" 
    class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border">
      <li
        v-for="address in (Array.isArray(passenger.filteredPickupAddresses) ? passenger.filteredPickupAddresses.slice(0, 3) : [])"
        :key="address.combined"
        @click="selectPickupAddress(address, passenger)"
        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
      >
        {{ address.combined }}
      </li>
    </ul>

<!-- Opcja: Ręczne wpisywanie -->
<div class="flex items-center">
  <input
    type="radio"
    :id="'pickup-manual-' + index"
    v-model="passenger.pickupOption"
    value="manual"
    @change="setPickupAddress(passenger)"
    class="mr-2"
  />
  <label :for="'pickup-manual-' + index" class="text-gray-700 dark:text-dark-text">
    Ręczne wpisywanie
  </label>
</div>
<input
  v-if="passenger.pickupOption === 'manual'"
  v-model="passenger.pickupAddress"
  type="text"
  placeholder="Wpisz adres odbioru"
  @input="filterAddressSuggestions(passenger, 'pickup')"
  @focus="showPickupSuggestions = true"
  @blur="hidePickupSuggestions(passenger)"
  class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text mt-2"
/>

<p v-if="passenger.pickupOption === 'home'" class="mt-2">
  Wybrany Adres: {{ passenger.pickupAddress }}
</p>

<!-- Lista podpowiedzi -->
<ul v-if="showPickupSuggestions && passenger.pickupOption === 'manual'" 
    class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border">
  <li
    v-for="address in (Array.isArray(passenger.filteredPickupAddresses) ? passenger.filteredPickupAddresses.slice(0, 3) : [])"
    :key="address.combined"
    @click="selectPickupAddress(address, passenger)"
    class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
  >
    {{ address.combined }}
  </li>
</ul>
  </div>
</div>


<!-- Adres Dowozu Opcje -->
<div>
  <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">
    Adres Dowozu:
  </label>
  <div class="space-y-2">
    <!-- Opcja: Adres domowy -->
    <div class="flex items-center">
      <input
        type="radio"
        :id="'dropoff-home-' + index"
        v-model="passenger.dropoffOption"
        value="home"
        @change="setDropoffAddress(passenger)"
        class="mr-2"
      />
      <label :for="'dropoff-home-' + index" class="text-gray-700 dark:text-dark-text">
        Z Pracy (Adres Domu)
      </label>
    </div>

    <!-- Opcja: Adres pracy -->
    <div class="flex items-center">
      <input
        type="radio"
        :id="'dropoff-work-' + index"
        v-model="passenger.dropoffOption"
        value="work"
        @change="setDropoffAddress(passenger)"
        class="mr-2"
      />
      <label :for="'dropoff-work-' + index" class="text-gray-700 dark:text-dark-text">
        Do Pracy (Adres Pracy)
      </label>
    </div>

    <!-- Pole tekstowe do wpisania adresu pracy -->
    <input
      v-if="passenger.dropoffOption === 'work'"
      v-model="passenger.dropoffAddress"
      type="text"
      placeholder="Wpisz adres pracy"
      @input="filterAddressSuggestions(passenger, 'dropoff')"
      @focus="showDropoffSuggestions = true"
      @blur="hideDropoffSuggestions(passenger)"
      class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text mt-2"
    />

    <!-- Lista podpowiedzi dla opcji 'Do Pracy (Adres Pracy)' -->
    <ul 
      v-if="showDropoffSuggestions && passenger.dropoffOption === 'work'"
      class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border"
    >
      <li
        v-for="address in (Array.isArray(passenger.filteredDropoffAddresses) ? passenger.filteredDropoffAddresses.slice(0, 3) : [])"
        :key="address.combined"
        @click="selectDropoffAddress(address, passenger)"
        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
      >
        {{ address.combined }}
      </li>
    </ul>

    <!-- Opcja: Ręczne wpisywanie -->
    <div class="flex items-center">
      <input
        type="radio"
        :id="'dropoff-manual-' + index"
        v-model="passenger.dropoffOption"
        value="manual"
        @change="setDropoffAddress(passenger)"
        class="mr-2"
      />
      <label :for="'dropoff-manual-' + index" class="text-gray-700 dark:text-dark-text">
        Ręczne wpisywanie
      </label>
    </div>

    <!-- Pole tekstowe do ręcznego wpisania adresu -->
    <input
      v-if="passenger.dropoffOption === 'manual'"
      v-model="passenger.dropoffAddress"
      type="text"
      placeholder="Wpisz adres dowozu"
      @input="filterAddressSuggestions(passenger, 'dropoff')"
      @focus="showDropoffSuggestions = true"
      @blur="hideDropoffSuggestions(passenger)"
      class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text mt-2"
    />

    <!-- Lista podpowiedzi dla ręcznego wpisywania adresu -->
    <ul 
      v-if="showDropoffSuggestions && passenger.dropoffOption === 'manual'"
      class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border"
    >
      <li
        v-for="address in (Array.isArray(passenger.filteredDropoffAddresses) ? passenger.filteredDropoffAddresses.slice(0, 3) : [])"
        :key="address.combined"
        @click="selectDropoffAddress(address, passenger)"
        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
      >
        {{ address.combined }}
      </li>
    </ul>

    <!-- Wybrany adres dla opcji "home" -->
    <p v-if="passenger.dropoffOption === 'home'" class="mt-2">
      Wybrany Adres: {{ passenger.dropoffAddress }}
    </p>
  </div>
</div>

          </div>
        </div>

        <!-- Formularz dodawania nowego pasażera -->
        <div class="mt-4 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Pasażer:</label>
            <input
                v-model="currentPassenger.passengerName"
                @input="filterPassengers"
                @focus="showPassengerSuggestions = true"
                @blur="hidePassengerSuggestions"
                type="text"
                placeholder="Wybierz pasażera"
                class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
                :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.passengerId }"
            />
            <span v-if="localErrors.passengerId" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.passengerId }}</span>

            <ul v-if="showPassengerSuggestions && filteredPassengers.length && currentPassenger.passengerName" class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border">
              <li
                  v-for="passenger in filteredPassengers"
                  :key="passenger.id"
                  @mousedown.prevent="selectPassenger(passenger)"
                  class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border"
              >
                {{ passenger.firstName }} {{ passenger.lastName }}
              </li>
            </ul>
          </div>

          <!-- Godzina odbioru -->
          <div v-if="currentPassenger.passengerId">
            <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Godzina odbioru:</label>
            <input
                v-model="currentPassenger.pickupTime"
                @click="showTimePicker"
                type="time"
                class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
            />
          </div>


          <!-- Adres Odbioru Opcje -->
<div v-if="currentPassenger.passengerId">
  <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">
    Adres Odbioru:
  </label>
  <div class="space-y-2">
    <!-- Opcja: Adres domowy -->
    <div class="flex items-center">
      <input
        type="radio"
        id="pickup-home-new"
        v-model="pickupOption"
        value="home"
        @change="setPickupAddress(currentPassenger)"
        class="mr-2"
      />
      <label for="pickup-home-new" class="text-gray-700 dark:text-dark-text">
        Do pracy (Adres zamieszkania)
      </label>
    </div>

    <!-- Opcja: Adres pracy -->
    <div class="flex items-center">
      <input
        type="radio"
        id="pickup-home-new"
        v-model="pickupOption"
        value="work"
        @change="setPickupAddress(currentPassenger)"
        class="mr-2"
      />
      <label for="pickup-home-new" class="text-gray-700 dark:text-dark-text">
        Z pracy (Adres pracy)
      </label>
    </div>
    <input
      v-if="pickupOption === 'work'"
      v-model="currentPassenger.pickupAddress"
      type="text"
      placeholder="Wpisz adres pracy"
      @input="filterNewAddressSuggestions(currentPassenger, 'pickup')"
      @focus="showPickupSuggestions = true"
      @blur="hidePickupSuggestions(currentPassenger)"
      class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
    />

    <ul v-if="showPickupSuggestions && pickupOption === 'work'" 
    class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border">
      <li
        v-for="address in (Array.isArray(currentPassenger.filteredPickupAddresses) ? currentPassenger.filteredPickupAddresses.slice(0, 3) : [])"
        :key="address.combined"
        @click="selectPickupAddress(address, currentPassenger)"
        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
      >
        {{ address.combined }}
      </li>
    </ul>

<!-- Opcja: Ręczne wpisywanie -->
<div class="flex items-center">
  <input
    type="radio"
    id="pickup-manual-new"
    v-model="pickupOption"
    value="manual"
    @change="setPickupAddress(currentPassenger)"
    class="mr-2"
  />
  <label for="pickup-manual-new" class="text-gray-700 dark:text-dark-text">
    Ręczne wpisywanie
  </label>
</div>
<input
  v-if="pickupOption === 'manual'"
  v-model="currentPassenger.pickupAddress"
  type="text"
  placeholder="Wpisz adres odbioru"
  @input="filterNewAddressSuggestions(currentPassenger, 'pickup')"
  @focus="showPickupSuggestions = true"
  @blur="hidePickupSuggestions(currentPassenger)"
  class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text mt-2"
/>

<p v-if="pickupOption === 'home'" class="mt-2">
  Wybrany Adres: {{ currentPassenger.pickupAddress }}
</p>

<!-- Lista podpowiedzi -->
<ul v-if="showPickupSuggestions && pickupOption === 'manual'" 
    class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border">
  <li
    v-for="address in (Array.isArray(currentPassenger.filteredPickupAddresses) ? currentPassenger.filteredPickupAddresses.slice(0, 3) : [])"
    :key="address.combined"
    @click="selectPickupAddress(address, currentPassenger)"
    class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
  >
    {{ address.combined }}
  </li>
</ul>
  </div>
</div>


<!-- Adres Dowozu Opcje -->
<div v-if="currentPassenger.passengerId">
  <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">
    Adres Dowozu:
  </label>
  <div class="space-y-2">
    <!-- Opcja: Adres domowy -->
    <div class="flex items-center">
      <input
        type="radio"
        id="dropoff-home-new"
        v-model="dropoffOption"
        value="home"
        @change="setDropoffAddress(currentPassenger)"
        class="mr-2"
      />
      <label for="dropoff-home-new" class="text-gray-700 dark:text-dark-text">
        Z Pracy (Adres Domu)
      </label>
    </div>

    <!-- Opcja: Adres pracy -->
    <div class="flex items-center">
      <input
        type="radio"
        id="dropoff-work-new"
        v-model="dropoffOption"
        value="work"
        @change="setDropoffAddress(currentPassenger)"
        class="mr-2"
      />
      <label for="dropoff-work-new" class="text-gray-700 dark:text-dark-text">
        Do Pracy (Adres Pracy)
      </label>
    </div>

    <!-- Pole tekstowe do wpisania adresu pracy -->
    <input
      v-if="dropoffOption === 'work'"
      v-model="currentPassenger.dropoffAddress"
      type="text"
      placeholder="Wpisz adres pracy"
      @input="filterNewAddressSuggestions(currentPassenger, 'dropoff')"
      @focus="showDropoffSuggestions = true"
      @blur="hideDropoffSuggestions(currentPassenger)"
      class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text mt-2"
    />

    <!-- Lista podpowiedzi dla opcji 'Do Pracy (Adres Pracy)' -->
    <ul 
      v-if="showDropoffSuggestions && dropoffOption === 'work'"
      class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border"
    >
      <li
        v-for="address in (Array.isArray(currentPassenger.filteredDropoffAddresses) ? currentPassenger.filteredDropoffAddresses.slice(0, 3) : [])"
        :key="address.combined"
        @click="selectDropoffAddress(address, currentPassenger)"
        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
      >
        {{ address.combined }}
      </li>
    </ul>

    <!-- Opcja: Ręczne wpisywanie -->
    <div class="flex items-center">
      <input
        type="radio"
        id="dropoff-manual-new"
        v-model="dropoffOption"
        value="manual"
        @change="setDropoffAddress(currentPassenger)"
        class="mr-2"
      />
      <label for="dropoff-manual-new" class="text-gray-700 dark:text-dark-text">
        Ręczne wpisywanie
      </label>
    </div>

    <!-- Pole tekstowe do ręcznego wpisania adresu -->
    <input
      v-if="dropoffOption === 'manual'"
      v-model="currentPassenger.dropoffAddress"
      type="text"
      placeholder="Wpisz adres dowozu"
      @input="filterNewAddressSuggestions(currentPassenger, 'dropoff')"
      @focus="showDropoffSuggestions = true"
      @blur="hideDropoffSuggestions(currentPassenger)"
      class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text mt-2"
    />

    <!-- Lista podpowiedzi dla ręcznego wpisywania adresu -->
    <ul 
      v-if="showDropoffSuggestions && dropoffOption === 'manual'"
      class="bg-white border border-gray-300 rounded-md mt-1 dark:bg-dark-box-bg dark:border-dark-border"
    >
      <li
        v-for="address in (Array.isArray(currentPassenger.filteredDropoffAddresses) ? currentPassenger.filteredDropoffAddresses.slice(0, 3) : [])"
        :key="address.combined"
        @click="selectDropoffAddress(address, currentPassenger)"
        class="p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border-hover"
      >
        {{ address.combined }}
      </li>
    </ul>

    <!-- Wybrany adres dla opcji "home" -->
    <p v-if="dropoffOption === 'home'" class="mt-2">
      Wybrany Adres: {{ currentPassenger.dropoffAddress }}
    </p>
  </div>
</div>

          <!-- Przycisk dodawania nowego pasażera -->
          <button v-if="currentPassenger.passengerId" @click="addPassenger" type="button" class="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-500 dark:bg-teal-500 dark:hover:bg-teal-600 transition-colors">
            Dodaj pasażera
          </button>
        </div>

        <!-- Kierowca -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Kierowca:</label>
          <select
              v-model="localOrder.driverId"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.driverId }"
              @change="validateField('driverId')"
          >
            <option :value=null>Wybierz kierowcę</option>
            <option v-for="driver in drivers" :key="driver.id" :value="driver.id">
              {{ driver.firstName }} {{ driver.lastName }}
            </option>
          </select>
          <span v-if="localErrors.driverId" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.driverId }}</span>
        </div>

        <!-- Notatki -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Notatki Dypsozytora:</label>
          <textarea
              v-model="localOrder.notesDispatcher"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.notesDispatcher }"
              @input="validateField('notes')"
          ></textarea>
          <span v-if="localErrors.notesDispatcher" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.notesDispatcher }}</span>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Notatki Kierowcy:</label>
          <textarea
              v-model="localOrder.notesDriver"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.notesDriver }"
              @input="validateField('notesDriver')"
          ></textarea>
          <span v-if="localErrors.notesDriver" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.notesDriver }}</span>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Notatki Specyfikacji:</label>
          <textarea
              v-model="localOrder.notesSpec"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.notesSpec }"
              @input="validateField('notesSpec')"
          ></textarea>
          <span v-if="localErrors.notesSpec" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.notesSpec }}</span>
        </div>


          <div v-if="localOrder.company?.onlyAB">
            <label class="block text-sm font-medium">KM Łączne</label>
            <input
              v-model.number="localOrder.distanceInKm"
              type="number"
              min="0"
              step="1"
              class="w-full p-2 border rounded dark:bg-dark-bg dark:text-dark-text dark:border-dark-border"
            />
          </div>

          <!-- KM dla onlyAB = false -->
          <div v-else>
            <label class="block text-sm font-medium">KM Łączne</label>
            <input
              v-model.number="localOrder.dispatcherKilometers"
              type="number"
              min="0"
              step="1"
              class="w-full p-2 border rounded dark:bg-dark-bg dark:text-dark-text dark:border-dark-border"
            />
          </div>
            <div class="flex items-center gap-2">
            <input
                v-model="localOrder.highway"
                type="checkbox"
                class="h-5 w-5 text-teal-600 dark:text-teal-400"
            />
            <label class="text-sm font-medium">Autostrada</label>
            </div>

          <div class="grid grid-cols-1 gap-4">
            <div>
              <label class="block text-sm font-medium">Cena za Km</label>
              <input
                v-model.number="localOrder.pricePerKilometer"
                type="number"
                min="0"
                step="0.01"
                class="w-full p-2 border rounded dark:bg-dark-bg dark:text-dark-text dark:border-dark-border"
              />
            </div>
            <div>
              <label class="block text-sm font-medium">Bramki</label>
              <input
                v-model.number="localOrder.driverGates"
                type="number"
                min="0"
                step="0.01"
                class="w-full p-2 border rounded dark:bg-dark-bg dark:text-dark-text dark:border-dark-border"
              />
            </div>
            <div>
              <label class="block text-sm font-medium">Dodatki</label>
              <input
                v-model.number="localOrder.additvies"
                type="number"
                min="0"
                step="0.01"
                class="w-full p-2 border rounded dark:bg-dark-bg dark:text-dark-text dark:border-dark-border"
              />
            </div>
            <div>
              <label class="block text-sm font-medium">Ryczałt</label>
              <input
                v-model.number="localOrder.flatRate"
                type="number"
                min="0"
                step="0.01"
                class="w-full p-2 border rounded dark:bg-dark-bg dark:text-dark-text dark:border-dark-border"
              />
            </div>
            <div>
            <label class="block text-sm font-medium">Suma Łączna</label>
            <input
              :value="calculatedTotalAmount"
              type="number"
              min="0"
              step="0.01"
              class="w-full p-2 border rounded dark:bg-dark-bg dark:text-dark-text dark:border-dark-border bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
              readonly
            />
          </div>
          </div>

        <!-- Status zamówienia -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1 dark:text-dark-text">Status zamówienia:</label>
          <select
              v-model="localOrder.orderStatus"
              class="w-full p-2 border rounded-md focus:outline-none focus:ring dark:bg-dark-box-bg dark:border-dark-border dark:text-dark-text"
              :class="{ 'border-red-500 dark:border-dark-status-unavailable': localErrors.orderStatus }"
              @change="validateField('orderStatus')"
          >
            <option value="DO_OBSLUGI" class="text-violet-500 dark:text-violet-500">Do Obsługi</option>
            <option value="WYSŁANE" class="text-orange-500 dark:text-orange-500">Wysłane</option>
            <option value="W_TRAKCIE" class="text-yellow-500 dark:text-yellow-500">W trakcie</option>
            <option value="PRZYJĘTE" class="text-blue-500 dark:text-blue-500">Przyjęte</option>
            <option value="ZAKONCZONY" class="text-red-500 dark:text-red-500">Zakończony</option>
          </select>
          <span v-if="localErrors.orderStatus" class="text-red-500 text-sm mt-1 dark:text-dark-status-unavailable">{{ localErrors.orderStatus }}</span>
        </div>

        <!-- Submit -->
        <div class="flex justify-end">
          <button type="submit"
                  class="w-full bg-button-bg text-button-text py-2 rounded-md hover:bg-button-hover focus:outline-none focus:ring focus:ring-indigo-500 dark:bg-dark-button-bg dark:hover:bg-dark-button-hover">
            Zapisz zmiany
          </button>

        </div>

      </form>
    </div>
  </div>
</template>

<style scoped>
/* Pasek przewijania w trybie ciemnym */
.dark .overflow-y-auto::-webkit-scrollbar {
  width: 8px; /* Szerokość paska przewijania */
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #2c7a7a; /* Teal-700 */
  border-radius: 8px; /* Zaokrąglenie suwaka */
  border: 2px solid #1a5d5d; /* Ciemniejszy odcień teal dla krawędzi suwaka */
}

.dark .overflow-y-auto::-webkit-scrollbar-track {
  background-color: #333333; /* Ciemne tło dla śledzenia paska */
  border-radius: 8px; /* Zaokrąglenie tła paska */
}

/* Pasek przewijania w trybie jasnym */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px; /* Szerokość paska przewijania */
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #d1d5db; /* Jasnoszary kolor suwaka (szary) */
  border-radius: 8px; /* Zaokrąglenie suwaka */
  border: 2px solid #9ca3af; /* Ciemniejszy szary dla krawędzi suwaka */
}

.overflow-y-auto::-webkit-scrollbar-track {
  background-color: #f3f4f6; /* Jasnoszare tło dla śledzenia paska */
  border-radius: 8px; /* Zaokrąglenie tła paska */
}
</style>

<script>
import passengersService from '@/services/passengersService';
import stationService from '@/services/stationService';

export default {
  props: {
    isOpen: Boolean,
    orderToEdit: Object,
    errors: Object,
    companies: {
      type: Array,
      required: true
    },
    passengers: {
      type: Array,
      required: true
    },
    drivers: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      localOrder: {
        ...(this.orderToEdit || {}),
        passenger: (this.orderToEdit?.passenger || []).map(passenger => {
        return {
          ...passenger,
          isEditing: false,
          pickupTime: passenger.pickupTime || '',
          pickupAddress: passenger.pickupAddress || '',
          dropoffAddress: passenger.dropoffAddress || '',
          city: passenger.city || '',
          homeAddress: '', // Przypisz homeAddress
          workAddress: '', // Przypisz workAddress
          filteredPickupAddresses: [],
          filteredDropoffAddresses: []
        }
      }),
      },
      localErrors: { ...this.errors },
      currentPassenger: {
        passengerName: '',
        firstName: '',
        lastName: '',
        passengerId: null,
        pickupTime: '',
        pickupAddress: '',
        pickupOption: '',
        dropoffOption: '',
        dropoffAddress: '',
        city: '',
        homeAddress: '',
        workAddress: '',
        filteredPickupAddresses: [],
        filteredDropoffAddresses: []
      },
      filteredPassengers: [],
      stations: [],
      filteredPickupAddresses: [],
      filteredDropoffAddresses: [],
      showPassengerSuggestions: false,
      isSubmitting: false,
      pickupOption: '',
      dropoffOption: '',
      showPickupSuggestions: false,
      showDropoffSuggestions: false
    };
  },
  computed: {
    calculatedTotalAmount() {
      const km = this.localOrder.company?.onlyAB
        ? this.localOrder.distanceInKm || 0
        : this.localOrder.dispatcherKilometers || 0;
      const pricePerKm = Number(this.localOrder.pricePerKilometer) || 0;
      const gates = Number(this.localOrder.driverGates) || 0;
      const additvies = Number(this.localOrder.additvies) || 0;
      const flatRate = Number(this.localOrder.flatRate) || 0;

      const total = (km * pricePerKm) + gates + additvies + flatRate;
      return isNaN(total) ? 0 : Number(total.toFixed(2));
    },
  },
  watch: {
    orderToEdit: {
      handler(newVal) {
        if (newVal) {
          this.localOrder = { ...newVal };
          this.localOrder.companyId = newVal.company ? newVal.company.id : null;
          // this.localOrder.passengerId = newVal.passenger ? newVal.passenger.id : null;
          this.localOrder.driverId = newVal.driver ? newVal.driver.id : null;
        }
      },
      immediate: true,
      deep: true
    }
  },
  async mounted() {
    try {
      const stationsData = await stationService.getAllStations();
      this.stations = stationsData.data;
    } catch (error) {
      console.error('Nie udało się pobrać stacji:', error);
    }

    this.currentPassenger = this.passengers.map(passenger => ({
      ...passenger,
      isEditing: false,
      pickupTime: passenger.pickupTime || '',
      pickupAddress: passenger.pickupAddress || '',
      dropoffAddress: passenger.dropoffAddress || '',
      city: passenger.city || '',
      homeAddress: passenger.homeAddress || '',
      workAddress: passenger.workAddress || '',
      filteredPickupAddresses: [],
      filteredDropoffAddresses: []
    }));
  },
  methods: {
    toggleEditMode(index) {
      const passenger = this.localOrder.passenger[index];

      if (passenger.isEditing) {
        // Jeśli użytkownik kończy edycję, resetuj pola
        passenger.dropoffOption = '';
        passenger.pickupOption = '';
        // passenger.pickupTime = '';
      }

      // Przełącz tryb edycji
      passenger.isEditing = !passenger.isEditing;
    },
    removePassenger(index) {
      // Usuń pasażera z listy
      this.localOrder.passenger.splice(index, 1);
    },
    closeModal() {
      this.$emit('close');
    },
    async submitForm() {
      if (this.isSubmitting || !this.localOrder) return;

      this.isSubmitting = true;
      this.validateField('mpk');
      this.validateField('companyId');
      // this.validateField('passengerId');
      // this.validateField('driverId');
      this.validateField('orderStatus');

      this.localOrder.totalAmount = this.calculatedTotalAmount;

      console.log(this.localOrder)

      // if (!Object.values(this.localErrors).some(error => error !== '')) {
      this.$emit('submit', this.localOrder);
      // }

      this.isSubmitting = false;
    },
    filterAddressSuggestions(passenger, type) {
      let query = '';
      let addresses = [];
      let allSuggestions = [];

      if (!Array.isArray(this.stations)) {
        console.error('Stacje nie są tablicą:', this.stations);
        return;
      }

      const fullPassenger = this.passengers.find(p => p.id === passenger.passengerId) || {
        homeAddress: '',
        workAddress: ''
      };

      if (!Array.isArray(passenger.filteredPickupAddresses)) {
        passenger.filteredPickupAddresses = [];
      }

      if (type === 'pickup') {
        query = passenger.pickupAddress.toLowerCase();
        addresses = [fullPassenger.homeAddress, fullPassenger.workAddress].filter(addr => addr);
        const filteredAddresses = addresses.filter(address => address.toLowerCase().startsWith(query));
        const filteredStations = this.stations.filter(station => station.name.toLowerCase().startsWith(query));
        allSuggestions = [
          ...filteredAddresses.map(addr => ({ combined: addr, name: addr, postcode: '' })),
          ...filteredStations.map(station => ({ 
            combined: `${station.name} ${station.postcode || 'Brak kodu'}`, 
            name: station.name, 
            postcode: station.postcode || 'Brak kodu' 
          }))
        ];
        passenger.filteredPickupAddresses = allSuggestions;
      } else if (type === 'dropoff') {
        query = passenger.dropoffAddress.toLowerCase();
        addresses = [fullPassenger.homeAddress, fullPassenger.workAddress].filter(addr => addr);
        const filteredAddresses = addresses.filter(address => address.toLowerCase().startsWith(query));
        const filteredStations = this.stations.filter(station => station.name.toLowerCase().startsWith(query));
        allSuggestions = [
          ...filteredAddresses.map(addr => ({ combined: addr, name: addr, postcode: '' })),
          ...filteredStations.map(station => ({ 
            combined: `${station.name} ${station.postcode || 'Brak kodu'}`, 
            name: station.name, 
            postcode: station.postcode || 'Brak kodu' 
          }))
        ];
        passenger.filteredDropoffAddresses = allSuggestions;
      }
    },
    filterNewAddressSuggestions(passenger, type) {
      let query = '';
      let addresses = [];
      let allSuggestions = [];

      if (!Array.isArray(this.stations)) {
        console.error('Stacje nie są tablicą:', this.stations);
        return;
      }

      const fullPassenger = this.passengers.find(p => p.id === passenger.passengerId) || {
        homeAddress: '',
        workAddress: ''
      };

      if (!Array.isArray(passenger.filteredPickupAddresses)) {
        passenger.filteredPickupAddresses = [];
      }

      if (type === 'pickup') {
        query = passenger.pickupAddress.toLowerCase();
        addresses = [fullPassenger.homeAddress, fullPassenger.workAddress].filter(addr => addr);
        const filteredAddresses = addresses.filter(address => address.toLowerCase().startsWith(query));
        const filteredStations = this.stations.filter(station => station.name.toLowerCase().startsWith(query));
        allSuggestions = [
          ...filteredAddresses.map(addr => ({ combined: addr, name: addr, postcode: '' })),
          ...filteredStations.map(station => ({ 
            combined: `${station.name} ${station.postcode || 'Brak kodu'}`, 
            name: station.name, 
            postcode: station.postcode || 'Brak kodu' 
          }))
        ];
        passenger.filteredPickupAddresses = allSuggestions;
      } else if (type === 'dropoff') {
        query = passenger.dropoffAddress.toLowerCase();
        addresses = [fullPassenger.homeAddress, fullPassenger.workAddress].filter(addr => addr);
        const filteredAddresses = addresses.filter(address => address.toLowerCase().startsWith(query));
        const filteredStations = this.stations.filter(station => station.name.toLowerCase().startsWith(query));
        allSuggestions = [
          ...filteredAddresses.map(addr => ({ combined: addr, name: addr, postcode: '' })),
          ...filteredStations.map(station => ({ 
            combined: `${station.name} ${station.postcode || 'Brak kodu'}`, 
            name: station.name, 
            postcode: station.postcode || 'Brak kodu' 
          }))
        ];
        passenger.filteredDropoffAddresses = allSuggestions;
      }
    },
    selectPickupAddress(address, passenger) {
      passenger.pickupAddress = address.combined;
      this.showPickupSuggestions = false; // Ukryj listę podpowiedzi
    },
    selectDropoffAddress(address, passenger) {
      passenger.dropoffAddress = address.combined;
      this.showDropoffSuggestions = false; // Ukryj listę podpowiedzi
    },
    hidePickupSuggestions() {
      setTimeout(() => {
        this.showPickupSuggestions = false;
      }, 100);
    },
    hideDropoffSuggestions() {
      setTimeout(() => {
        this.showDropoffSuggestions = false;
      }, 100);
    },
    async getPassengerById(id) {
      try {
        const passengerData = await passengersService.getPassengerById(id);
        return passengerData.data;
      } catch (error) {
        console.error(error)
      }
    },
    filterPassengers() {
      const query = this.currentPassenger.passengerName.toLowerCase();

      if (query) {
        this.filteredPassengers = this.passengers
          .filter(passenger =>
            (passenger.firstName + ' ' + passenger.lastName).toLowerCase().includes(query)
          )
          .slice(0, 3); // Ograniczenie liczby wyników do 3
        this.showPassengerSuggestions = this.filteredPassengers.length > 0;
      } else {
        this.showPassengerSuggestions = false;
      }
    },
  selectPassenger(passenger) {
    this.currentPassenger.passengerName = `${passenger.firstName} ${passenger.lastName}`;
    this.currentPassenger.firstName = passenger.firstName;
    this.currentPassenger.lastName = passenger.lastName;
    this.currentPassenger.passengerId = passenger.id;
    this.currentPassenger.homeAddress = passenger.homeAddress;
    this.currentPassenger.workAddress = passenger.workAddress;
    this.currentPassenger.city = passenger.city;
    this.showPassengerSuggestions = false;
  },
  hidePassengerSuggestions() {
    setTimeout(() => {
      this.showPassengerSuggestions = false;
    }, 200);
  },
  setPickupAddress() {
      if (this.pickupOption === 'home') {
        this.currentPassenger.pickupAddress = this.currentPassenger.homeAddress || 'Adres Zamieszkania';
      } else if (this.pickupOption === 'work') {
        this.currentPassenger.pickupAddress = '';
        this.filterNewAddressSuggestions(this.currentPassenger, 'pickup');
      } else if (this.pickupOption === 'manual') {
        this.currentPassenger.pickupAddress = '';
        this.filterNewAddressSuggestions(this.currentPassenger, 'pickup');
      }
    },
    setDropoffAddress() {
      if (this.dropoffOption === 'home') {
        this.currentPassenger.dropoffAddress = this.currentPassenger.homeAddress || 'Adres Domu';
      } else if (this.dropoffOption === 'work') {
        this.currentPassenger.dropoffAddress = '';
        this.filterNewAddressSuggestions(this.currentPassenger, 'dropoff');
      } else if (this.dropoffOption === 'manual') {
        this.currentPassenger.dropoffAddress = ''; // Wyczyść pole przy ręcznym wpisywaniu
        this.filterNewAddressSuggestions(this.currentPassenger, 'dropoff');
      }
    },
  addPassenger() {
    if (!Array.isArray(this.localOrder.passenger)) {
      this.localOrder.passenger = [];
    }
    this.localOrder.passenger.push({ ...this.currentPassenger, isEditing: false });
    this.resetCurrentPassenger();
  },
  showTimePicker(event) {
      event.target.showPicker();
  },
  resetCurrentPassenger() {
    this.currentPassenger = {
      passengerName: '',
      firstName: '',
      lastName: '',
      passengerId: null,
      pickupTime: '',
      pickupAddress: '',
      dropoffAddress: '',
      homeAddress: '',
      workAddress: '',
      city: ''
    };
    this.pickupOption = '';
    this.dropoffOption = '';
  },
    validateField(field) {
      const order = this.localOrder;
      switch (field) {
        case 'mpk':
          this.localErrors.mpk = order.mpk.length < 2 ? 'MPK musi mieć co najmniej 2 znaki' : '';
          break;
        case 'companyId':
          this.localErrors.companyId = !order.companyId ? 'Firma jest wymagana' : '';
          break;
        case 'orderStatus':
          this.localErrors.orderStatus = !order.orderStatus ? 'Status zamówienia jest wymagany' : '';
          break;
      }
    },
    filterWorkAddresses() {
      this.filteredWorkAddresses = this.stations.map(station => station.name);
    },
  }
};
</script>
